import { call, put, select, take, takeEvery } from 'redux-saga/effects'

import { ToastTemplateTicked } from '@components/UI/ToastTemplate'
import { BrandFanFilters } from '@enums'
import { selectAuthToken } from '@store/auth'
import {
  fetchInfluencerIdentity,
  FetchInfluencerIdentityActionTypes,
  refreshBrandFanGroupMembers,
  refreshInfluencerIdentitiesSelf,
  removeBrandFanGroupMember,
  RemoveBrandFanGroupMemberActionTypes,
  removeBrandFanGroupMemberError,
  removeBrandFanGroupMemberSuccess,
  selectBrandFanGroupName,
  selectBrandFanMember,
  selectBrandFanMemberId,
} from '@store/brandFans'
import { removeBrandFanMember } from '@store/brandFans/actions/removeBrandFanMember'
import { fetchSubmission, SubmissionActionTypes } from '@store/submissions'
import { Toaster } from '@tribegroup'
import { networkRequest } from '@utils'

export function* handleRemoveBrandFanGroupMember(
  action: ReturnType<typeof removeBrandFanGroupMember>,
) {
  const {
    identityId,
    groupId,
    submissionId,
    campaignId,
    influencerId,
    memberId,
    refetchIdentity,
  } = action.payload
  try {
    const authToken: string = yield select(selectAuthToken)

    const brandFanMemberId = yield select(selectBrandFanMemberId, groupId, identityId, influencerId)

    const brandFanMember: ReturnType<typeof selectBrandFanMember> = yield select(
      selectBrandFanMember,
      groupId,
      memberId ?? brandFanMemberId,
    )

    const removeBrandFanMemberLink = brandFanMember.links.find((link) => link.rel === 'remove')

    if (!removeBrandFanMemberLink) {
      throw new Error('Remove link not found')
    }
    yield call(
      networkRequest,
      removeBrandFanMemberLink.method,
      removeBrandFanMemberLink.href,
      undefined,
      authToken,
    )
    yield put(removeBrandFanMember(groupId, brandFanMemberId))

    if (submissionId && campaignId) {
      yield put(fetchSubmission(submissionId, campaignId))
      yield take(SubmissionActionTypes.FETCH_SUCCESS)
    } else {
      yield put(refreshBrandFanGroupMembers(groupId))
      yield put(refreshInfluencerIdentitiesSelf(BrandFanFilters.BrandFans))
      if (refetchIdentity) {
        yield put(fetchInfluencerIdentity(identityId))
        yield take(FetchInfluencerIdentityActionTypes.FETCH_SUCCESS)
      }
    }

    yield put(removeBrandFanGroupMemberSuccess(identityId, groupId))

    const brandFanGroupName = yield select(selectBrandFanGroupName, groupId)
    yield call(Toaster.createToast, ToastTemplateTicked, {
      i18nKey: 'brandFans.identityGroups.removeFromGroup.success',
      values: { brandFanGroupName },
    })
  } catch (error) {
    yield put(removeBrandFanGroupMemberError(groupId, error))
  }
}

export function* watchRemoveBrandFanGroupMemberRequest() {
  yield takeEvery(
    RemoveBrandFanGroupMemberActionTypes.REMOVE_MEMBER_REQUEST,
    handleRemoveBrandFanGroupMember,
  )
}
